import {isNotNil} from 'ramda';

import {invalidateOnSuccess, omneticApi as api} from '@dms/api/base';

import {
  createTestDriveWithBusinessCaseResponseSchema,
  CreateTestDriveApiArg,
  CreateTestDriveApiResponse,
  CreateTestDriveWithBusinessCaseApiArg,
  CreateTestDriveWithBusinessCaseApiResponse,
  GetTestDriveApiResponse,
  GetTestDriveApiArg,
  ListTestDriveApiResponse,
  ListTestDriveApiArg,
} from './types';

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBusinessCaseTestDrive: build.query<GetTestDriveApiResponse, GetTestDriveApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/test-drive`,
        params: {businessCaseId: queryArg.businessCaseId},
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'BusinessCaseTestDrive', id: queryArg.businessCaseId},
      ],
    }),
    createTestDrive: build.mutation<CreateTestDriveApiResponse, CreateTestDriveApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/test-drive`,
        method: 'POST',
        body: queryArg.createTestDriveRequestBody,
        params: {businessCaseId: queryArg.businessCaseId},
      }),
      invalidatesTags: (_result, _error, queryArg) => {
        const tags: Array<
          | {type: 'BusinessCaseTestDrive'; id: string}
          | {type: 'VehicleTestDrives'; id: string}
          | 'VehicleTestDrivesV1'
          | {type: 'BusinessCaseActivity'; id: string}
          | {type: 'TestDriveCalendar'}
        > = [
          {type: 'BusinessCaseTestDrive', id: queryArg.businessCaseId},
          {type: 'BusinessCaseActivity', id: queryArg.businessCaseId},
          {type: 'TestDriveCalendar'},
        ];
        if (queryArg.createTestDriveRequestBody.vehicleId) {
          tags.push(
            {type: 'VehicleTestDrives', id: queryArg.createTestDriveRequestBody.vehicleId},
            'VehicleTestDrivesV1'
          );
        }
        return tags;
      },
    }),
    createTestDriveWithBusinessCase: build.mutation<
      CreateTestDriveWithBusinessCaseApiResponse,
      CreateTestDriveWithBusinessCaseApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/test-drive/with-business-case`,
        method: 'POST',
        body: queryArg.createTestDriveWithBusinessCaseRequestBody,
      }),
      extraOptions: {responseSchema: createTestDriveWithBusinessCaseResponseSchema},
      invalidatesTags: invalidateOnSuccess(
        (
          result: CreateTestDriveWithBusinessCaseApiResponse | undefined,
          queryArg: CreateTestDriveWithBusinessCaseApiArg
        ) => {
          const {vehicleId} = queryArg.createTestDriveWithBusinessCaseRequestBody;
          return [
            ...(isNotNil(result)
              ? [
                  {type: 'BusinessCaseTestDrive' as const, id: result.businessCaseId},
                  {type: 'BusinessCaseActivity' as const, id: result.businessCaseId},
                ]
              : []),
            {type: 'TestDriveCalendar'},
            {type: 'VehicleTestDrives', id: vehicleId},
            'VehicleTestDrivesV1',
            ...(result?.isBusinessCaseCreated
              ? [
                  {type: 'VehicleLastActiveBC' as const, id: vehicleId},
                  {type: 'BusinessCaseCountByVehicle' as const, id: vehicleId},
                  'BusinessCaseCount' as const,
                  'InterestCount' as const,
                  'PersonnelOverviewBusinessCase' as const,
                ]
              : []),
          ];
        }
      ),
    }),
    listBusinessCaseTestDrives: build.query<ListTestDriveApiResponse, ListTestDriveApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/test-drive`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'BusinessCaseTestDrive', id: queryArg.businessCaseId},
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBusinessCaseTestDriveQuery,
  useCreateTestDriveMutation,
  useCreateTestDriveWithBusinessCaseMutation,
  useListBusinessCaseTestDrivesQuery,
} = injectedRtkApi;
